import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import clientAuthService from '../services/ClientAuthService';
import ErrorHandler from '../utils/ErrorHandler';

/**
 * Client Authentication Store
 * Manages client authentication state, login/logout, and session persistence
 */
export const useClientAuthStore = create(
  persist(
    (set, get) => ({
      // State
      isAuthenticated: false,
      client: null,
      isLoading: false,
      error: null,
      isInitialized: false,

      // Actions
      signup: async (clientData) => {
        set({ isLoading: true, error: null });
        
        try {
          const result = await clientAuthService.signup(clientData);
          
          if (result.success) {
            set({
              isLoading: false,
              error: null
            });
            return result;
          } else {
            throw new Error(result.message || 'Signup failed');
          }
        } catch (error) {
          const errorInfo = ErrorHandler.handleApiError(error, 'Client Signup');
          set({
            isLoading: false,
            error: errorInfo.message
          });
          throw error;
        }
      },

      login: async (credentials) => {
        set({ isLoading: true, error: null });
        
        try {
          const result = await clientAuthService.login(credentials);
          
          if (result.success) {
            set({
              isAuthenticated: true,
              client: result.client,
              isLoading: false,
              error: null
            });
            return result;
          } else {
            throw new Error(result.message || 'Login failed');
          }
        } catch (error) {
          const errorInfo = ErrorHandler.handleApiError(error, 'Client Login');
          set({
            isAuthenticated: false,
            client: null,
            isLoading: false,
            error: errorInfo.message
          });
          throw error;
        }
      },

      logout: () => {
        clientAuthService.logout();
        set({
          isAuthenticated: false,
          client: null,
          error: null,
          isInitialized: false // Reset initialization flag to allow re-login
        });
      },

      // Handle 401 errors - force logout and clear all data
      handle401Error: () => {
        console.log('[ClientAuthStore] Handling 401 error - forcing logout');
        clientAuthService.logout();
        set({
          isAuthenticated: false,
          client: null,
          error: 'Your session has expired. Please log in again.',
          isInitialized: true,
          isLoading: false
        });
        
        // Force a page reload to ensure clean state
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }, 1000);
      },

      initialize: async () => {
        if (get().isInitialized) return get().isAuthenticated;
        
        set({ isLoading: true });
        
        try {
          const isLoggedIn = await clientAuthService.initialize();
          const client = clientAuthService.getCurrentClient();
          
          set({
            isAuthenticated: isLoggedIn,
            client: client,
            isLoading: false,
            isInitialized: true,
            error: null
          });
          
          return isLoggedIn;
        } catch (error) {
          console.error('Client auth initialization error:', error);
          set({
            isAuthenticated: false,
            client: null,
            isLoading: false,
            isInitialized: true,
            error: null
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),

      // Session validation
      validateSession: async () => {
        try {
          const isValid = await clientAuthService.validateSession();
          if (!isValid) {
            get().logout();
          }
          return isValid;
        } catch (error) {
          console.error('Session validation error:', error);
          get().logout();
          return false;
        }
      }
    }),
    {
      name: 'client-auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        client: state.client
      })
    }
  )
);

